import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { 
  Type, 
  Plus, 
  Trash2, 
  ChevronDown,
  ChevronRight,
  Edit3,
  Eye,
  EyeOff,
  MapPin,
  Move,
  Building2,
  Navigation
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';

// Storage key for labels
const LABELS_STORAGE_KEY = 'matterport-custom-labels';

export function LabelManager({ 
  sdkInstance,
  cameraPosition,
  isSDKConnected = false,
  enabled = true,
  matterportLabels = [], // Labels from the Matterport model
  onNavigateToLabel, // Function to navigate to a label
}) {
  const { canEdit, isViewer } = useAuth();
  const [labels, setLabels] = useState(() => {
    const saved = localStorage.getItem(LABELS_STORAGE_KEY);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.warn('Failed to parse saved labels:', e);
      }
    }
    return [];
  });

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingLabel, setEditingLabel] = useState(null);
  const [isLabelsOpen, setIsLabelsOpen] = useState(true);
  const [isModelLabelsOpen, setIsModelLabelsOpen] = useState(true);
  const [newLabel, setNewLabel] = useState({
    text: '',
    fontSize: 0.5,
    useCurrentPosition: true,
    position: { x: 0, y: 1, z: 0 },
  });

  // Save labels to localStorage
  useEffect(() => {
    localStorage.setItem(LABELS_STORAGE_KEY, JSON.stringify(labels));
  }, [labels]);

  // Create label in SDK (if connected)
  const createLabelInSDK = async (labelData) => {
    if (!sdkInstance || !isSDKConnected) return null;

    try {
      // The Label module allows adding text labels in 3D space
      // Note: This is a simplified implementation - actual SDK may vary
      console.log('Creating label in SDK:', labelData);
      return null; // Return created label ID if SDK supports it
    } catch (e) {
      console.warn('Could not create label in SDK:', e);
      return null;
    }
  };

  const handleCreateLabel = async () => {
    if (!newLabel.text) return;

    const position = newLabel.useCurrentPosition && cameraPosition?.position
      ? cameraPosition.position
      : newLabel.position;

    const labelData = {
      id: `label-${Date.now()}`,
      text: newLabel.text,
      fontSize: newLabel.fontSize,
      position,
      createdAt: new Date().toISOString(),
      visible: true,
    };

    // Try to create in SDK
    const sdkLabelId = await createLabelInSDK(labelData);
    if (sdkLabelId) {
      labelData.sdkId = sdkLabelId;
    }

    setLabels(prev => [...prev, labelData]);
    setNewLabel({
      text: '',
      fontSize: 0.5,
      useCurrentPosition: true,
      position: { x: 0, y: 1, z: 0 },
    });
    setIsCreateDialogOpen(false);
  };

  const handleUpdateLabel = () => {
    if (!editingLabel) return;

    setLabels(prev => prev.map(l => 
      l.id === editingLabel.id ? editingLabel : l
    ));
    setEditingLabel(null);
  };

  const handleDeleteLabel = (labelId) => {
    setLabels(prev => prev.filter(l => l.id !== labelId));
  };

  const toggleLabelVisibility = (labelId) => {
    setLabels(prev => prev.map(l => 
      l.id === labelId ? { ...l, visible: !l.visible } : l
    ));
  };

  if (!enabled) return null;

  return (
    <div className="space-y-4">
      {/* Header with Create Button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Type className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium">3D Labels</span>
          <Badge variant="secondary" className="text-[10px]">
            {labels.length}
          </Badge>
        </div>
        
        {canEdit && (
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm" className="h-7 gap-1">
              <Plus className="h-3 w-3" />
              Add Label
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Create 3D Label</DialogTitle>
              <DialogDescription>
                Add a text label in the 3D space.
                {!isSDKConnected && (
                  <span className="block mt-1 text-yellow-500">
                    Note: Labels will be stored locally (SDK not fully connected)
                  </span>
                )}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="label-text">Label Text *</Label>
                <Input
                  id="label-text"
                  placeholder="e.g., Tank A, Control Panel..."
                  value={newLabel.text}
                  onChange={(e) => setNewLabel(prev => ({ ...prev, text: e.target.value }))}
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Font Size</Label>
                  <span className="text-xs text-muted-foreground">{newLabel.fontSize.toFixed(1)}m</span>
                </div>
                <Slider
                  value={[newLabel.fontSize]}
                  onValueChange={([value]) => setNewLabel(prev => ({ ...prev, fontSize: value }))}
                  min={0.1}
                  max={2}
                  step={0.1}
                />
              </div>

              <div className="space-y-2">
                <Label>Position</Label>
                <div className="flex items-center gap-2">
                  <Button
                    variant={newLabel.useCurrentPosition ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setNewLabel(prev => ({ ...prev, useCurrentPosition: true }))}
                    disabled={!cameraPosition}
                  >
                    <MapPin className="h-3 w-3 mr-1" />
                    Current View
                  </Button>
                  <Button
                    variant={!newLabel.useCurrentPosition ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setNewLabel(prev => ({ ...prev, useCurrentPosition: false }))}
                  >
                    <Move className="h-3 w-3 mr-1" />
                    Manual
                  </Button>
                </div>
                
                {!newLabel.useCurrentPosition && (
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    <div>
                      <Label className="text-xs">X</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={newLabel.position.x}
                        onChange={(e) => setNewLabel(prev => ({
                          ...prev,
                          position: { ...prev.position, x: parseFloat(e.target.value) || 0 }
                        }))}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Y</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={newLabel.position.y}
                        onChange={(e) => setNewLabel(prev => ({
                          ...prev,
                          position: { ...prev.position, y: parseFloat(e.target.value) || 0 }
                        }))}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Z</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={newLabel.position.z}
                        onChange={(e) => setNewLabel(prev => ({
                          ...prev,
                          position: { ...prev.position, z: parseFloat(e.target.value) || 0 }
                        }))}
                      />
                    </div>
                  </div>
                )}
                
                {cameraPosition && newLabel.useCurrentPosition && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Position: ({cameraPosition.position?.x?.toFixed(2)}, {cameraPosition.position?.y?.toFixed(2)}, {cameraPosition.position?.z?.toFixed(2)})
                  </p>
                )}
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateLabel} disabled={!newLabel.text}>
                Create Label
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {/* Viewer mode notice */}
      {isViewer && (
        <div className="text-xs text-muted-foreground bg-secondary/50 p-2 rounded">
          View-only access. Contact an admin to create or edit labels.
        </div>
      )}

      {/* Model Labels Section (from Matterport) */}
      {matterportLabels.length > 0 && (
        <Collapsible open={isModelLabelsOpen} onOpenChange={setIsModelLabelsOpen}>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" className="w-full justify-between p-2 h-auto">
              <div className="flex items-center gap-2">
                <Building2 className="h-3 w-3 text-primary" />
                <span className="text-xs font-medium text-muted-foreground">
                  Model Areas ({matterportLabels.length})
                </span>
              </div>
              {isModelLabelsOpen ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronRight className="h-3 w-3" />
              )}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-1">
            {matterportLabels.map((label, index) => (
              <div
                key={label.id || `model-label-${index}`}
                className="flex items-center gap-2 p-2 rounded-lg bg-primary/10 hover:bg-primary/20 transition-colors group cursor-pointer"
                onClick={() => onNavigateToLabel && onNavigateToLabel(label)}
              >
                <Building2 className="h-4 w-4 text-primary flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">
                    {label.text || label.label || 'Unnamed Area'}
                  </p>
                  {label.floor !== undefined && (
                    <p className="text-xs text-muted-foreground">
                      Floor {label.floor}
                    </p>
                  )}
                </div>
                {onNavigateToLabel && (
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onNavigateToLabel(label);
                    }}
                    title="Go to area"
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Navigation className="h-3 w-3" />
                  </Button>
                )}
              </div>
            ))}
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* Labels List */}
      {labels.length > 0 ? (
        <Collapsible open={isLabelsOpen} onOpenChange={setIsLabelsOpen}>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" className="w-full justify-between p-2 h-auto">
              <span className="text-xs font-medium text-muted-foreground">
                Your Labels ({labels.length})
              </span>
              {isLabelsOpen ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronRight className="h-3 w-3" />
              )}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-1">
            {labels.map((label) => (
              <div
                key={label.id}
                className="flex items-center gap-2 p-2 rounded-lg bg-secondary/50 hover:bg-secondary/80 transition-colors group"
              >
                <Type className="h-4 w-4 text-primary flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className={cn(
                    "text-sm font-medium truncate",
                    !label.visible && "text-muted-foreground line-through"
                  )}>
                    {label.text}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Size: {label.fontSize?.toFixed(1)}m
                  </p>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => toggleLabelVisibility(label.id)}
                    title={label.visible ? 'Hide label' : 'Show label'}
                  >
                    {label.visible ? (
                      <Eye className="h-3 w-3" />
                    ) : (
                      <EyeOff className="h-3 w-3" />
                    )}
                  </Button>
                  {canEdit && (
                    <>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => setEditingLabel(label)}
                        title="Edit label"
                      >
                        <Edit3 className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => handleDeleteLabel(label.id)}
                        title="Delete label"
                      >
                        <Trash2 className="h-3 w-3 text-destructive" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </CollapsibleContent>
        </Collapsible>
      ) : (
        <div className="text-center py-6">
          <Type className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">No labels yet</p>
          <p className="text-xs text-muted-foreground mt-1">
            Click "Add Label" to create a 3D text label
          </p>
        </div>
      )}

      {/* Edit Label Dialog */}
      <Dialog open={!!editingLabel} onOpenChange={(open) => !open && setEditingLabel(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Label</DialogTitle>
            <DialogDescription>
              Update this label's properties.
            </DialogDescription>
          </DialogHeader>
          
          {editingLabel && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-label-text">Label Text</Label>
                <Input
                  id="edit-label-text"
                  value={editingLabel.text}
                  onChange={(e) => setEditingLabel(prev => ({ ...prev, text: e.target.value }))}
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Font Size</Label>
                  <span className="text-xs text-muted-foreground">{editingLabel.fontSize?.toFixed(1)}m</span>
                </div>
                <Slider
                  value={[editingLabel.fontSize || 0.5]}
                  onValueChange={([value]) => setEditingLabel(prev => ({ ...prev, fontSize: value }))}
                  min={0.1}
                  max={2}
                  step={0.1}
                />
              </div>

              <div className="space-y-2">
                <Label>Position</Label>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-xs">X</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={editingLabel.position?.x || 0}
                      onChange={(e) => setEditingLabel(prev => ({
                        ...prev,
                        position: { ...prev.position, x: parseFloat(e.target.value) || 0 }
                      }))}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Y</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={editingLabel.position?.y || 0}
                      onChange={(e) => setEditingLabel(prev => ({
                        ...prev,
                        position: { ...prev.position, y: parseFloat(e.target.value) || 0 }
                      }))}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Z</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={editingLabel.position?.z || 0}
                      onChange={(e) => setEditingLabel(prev => ({
                        ...prev,
                        position: { ...prev.position, z: parseFloat(e.target.value) || 0 }
                      }))}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter className="flex justify-between">
            <Button 
              variant="destructive" 
              onClick={() => {
                if (editingLabel) {
                  handleDeleteLabel(editingLabel.id);
                  setEditingLabel(null);
                }
              }}
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Delete
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEditingLabel(null)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateLabel}>
                Save Changes
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default LabelManager;
